import React, { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';

export default function NewsletterSignup({ variant = 'default', leadMagnet = false }) {
  const [email, setEmail] = useState('');
  const [consent, setConsent] = useState(false);
  const [lang, setLang] = useState('en');
  const [status, setStatus] = useState('idle'); // idle | loading | success | error
  const [message, setMessage] = useState('');

  const apiBase = process.env.NODE_ENV === 'development' ? 'http://localhost:5019' : '';

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !consent) return;

    setStatus('loading');
    try {
      const res = await fetch(`${apiBase}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, consent, lang }),
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setMessage(data.message);
      } else {
        setStatus('error');
        setMessage(data.message);
      }
    } catch {
      setStatus('error');
      setMessage('Connection failed. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="text-center py-6">
        <div className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-4"
          style={{ backgroundColor: 'rgba(149, 107, 68, 0.15)', border: '1px solid rgba(149, 107, 68, 0.3)' }}>
          <Check size={20} style={{ color: '#c9a84c' }} />
        </div>
        <p className="font-serif text-lg" style={{ color: '#eae1d3' }}>{message}</p>
        {leadMagnet && (
          <p className="mt-3 text-sm" style={{ color: 'rgba(212, 197, 171, 0.55)' }}>
            Check your email for Chapter 1.
          </p>
        )}
      </div>
    );
  }

  const isCompact = variant === 'compact';

  return (
    <div className={isCompact ? '' : 'text-center'}>
      {!isCompact && (
        <>
          <h3 className="font-serif text-xl md:text-2xl mb-3" style={{ color: '#eae1d3' }}>
            {leadMagnet ? 'Get Chapter 1 Free' : 'Stay on the Path'}
          </h3>
          <p className="mb-6 leading-relaxed" style={{ color: 'rgba(212, 197, 171, 0.55)' }}>
            {leadMagnet
              ? 'Enter your email to receive the first chapter and occasional reflections on the Seven Patterns.'
              : 'Receive occasional reflections on the Seven Patterns, new chapters, and invitations to explore with the AI Life Guide.'}
          </p>
        </>
      )}

      <form onSubmit={handleSubmit} className={`flex flex-col ${isCompact ? 'sm:flex-row' : 'sm:flex-row justify-center'} gap-3 max-w-lg ${isCompact ? '' : 'mx-auto'}`}>
        <div className="relative flex-1">
          <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none" style={{ color: 'rgba(212, 197, 171, 0.3)' }} />
          <input
            type="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setStatus('idle'); }}
            placeholder="your@email.com"
            required
            className="w-full pl-10 pr-4 py-3 rounded-sm text-sm transition-all outline-none"
            style={{
              backgroundColor: 'rgba(42, 37, 32, 0.6)',
              border: '1px solid rgba(149, 107, 68, 0.2)',
              color: '#eae1d3',
            }}
            onFocus={e => e.target.style.borderColor = 'rgba(149, 107, 68, 0.5)'}
            onBlur={e => e.target.style.borderColor = 'rgba(149, 107, 68, 0.2)'}
          />
        </div>
        {leadMagnet && (
          <select
            value={lang}
            onChange={e => setLang(e.target.value)}
            className="px-3 py-3 rounded-sm text-sm outline-none cursor-pointer"
            style={{
              backgroundColor: 'rgba(42, 37, 32, 0.6)',
              border: '1px solid rgba(149, 107, 68, 0.2)',
              color: '#eae1d3',
            }}
          >
            <option value="en">English</option>
            <option value="cn">中文</option>
          </select>
        )}
        <button
          type="submit"
          disabled={status === 'loading' || !consent}
          className="px-6 py-3 rounded-sm text-sm tracking-wide transition-all disabled:opacity-40"
          style={{
            backgroundColor: 'rgba(149, 107, 68, 0.15)',
            border: '1px solid rgba(149, 107, 68, 0.35)',
            color: '#c9a84c',
          }}
        >
          {status === 'loading' ? <Loader2 size={16} className="animate-spin mx-auto" /> : (leadMagnet ? 'Send Chapter 1' : 'Subscribe')}
        </button>
      </form>

      <label className={`flex items-start gap-2 mt-3 cursor-pointer ${isCompact ? '' : 'justify-center'}`}>
        <input
          type="checkbox"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
          className="mt-1 accent-amber-700"
        />
        <span className="text-xs leading-relaxed" style={{ color: 'rgba(212, 197, 171, 0.4)' }}>
          I agree to receive emails. Unsubscribe anytime. We never share your data.
        </span>
      </label>

      {status === 'error' && (
        <p className="mt-2 text-sm" style={{ color: '#c97a4c' }}>{message}</p>
      )}
    </div>
  );
}
